/* eslint-disable react-refresh/only-export-components */
/** @format */

import type { PermissionKey } from './permission-keys';
import { usePermission } from './usePermission';

// ─── useCan Hook ─────────────────────────────────────────────────

interface UseCanOptions {
  /** If true and permission is an array, user needs ANY instead of ALL. */
  any?: boolean;
}

/**
 * Imperative permission check hook.
 *
 * Returns `true` if:
 * - No permission specified (always allowed)
 * - Dynamic permissions loaded AND user has the required permission(s)
 * - Dynamic permissions NOT loaded → returns false (wait for matrix)
 */
export function useCan(
  permission?: PermissionKey | PermissionKey[],
  options?: UseCanOptions,
): boolean {
  const { loaded, canAll, canAny } = usePermission();

  if (!permission) return true;

  if (!loaded) return false;

  const keys = Array.isArray(permission) ? permission : [permission];
  return options?.any ? canAny(keys) : canAll(keys);
}

// ─── <Can> Component ─────────────────────────────────────────────

interface CanProps {
  /** Single permission key or array of keys. */
  permission: PermissionKey | PermissionKey[];
  /** If true and permission is an array, user needs ANY instead of ALL. */
  any?: boolean;
  /** Custom UI to render when permission is denied (e.g., read-only view). */
  renderDenied?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Declarative permission wrapper.
 *
 * Renders children if allowed, renderDenied (or null) if denied.
 * No extra DOM nodes — uses Fragment.
 */
export function Can({ permission, any: anyMode, renderDenied = null, children }: CanProps) {
  const allowed = useCan(permission, { any: anyMode });

  if (!allowed) return <>{renderDenied}</>;

  return <>{children}</>;
}
